import type { FormEvent } from "react";
import { Icon } from "@iconify/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AppShell,
  Button,
  ButtonLink,
  EmptyState,
  Field,
  MetricStrip,
  PageHeader,
  ProgressMeter,
  StatusPill,
  Surface,
  TurnstileChallenge
} from "@moxipass/ui";
import { api, idempotencyKey } from "./api";

const nav = [
  { href: "/", label: "Wallet", icon: "solar:wallet-bold-duotone" },
  { href: "/inbox", label: "Inbox", icon: "solar:inbox-bold-duotone" },
  { href: "/profile", label: "Privacy", icon: "solar:shield-user-bold-duotone" }
];

const DEFAULT_MERCHANT_ID = "mer_01jz0000000000000000000000";
const DEFAULT_BRANCH_ID = "br_01jz0000000000000000000000";
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

function pathParts() {
  return window.location.pathname.split("/").filter(Boolean);
}

export function App() {
  const parts = pathParts();
  const active = `/${parts[0] ?? ""}`;
  const withActive = nav.map((item) => ({ ...item, active: item.href === active || (item.href === "/" && active === "/") }));

  return (
    <AppShell appName="MoxiPass" eyebrow="Wallet PWA" nav={withActive}>
      {parts[0] === "m" ? <MerchantLanding slug={parts[1] ?? "baan-cha"} /> : null}
      {parts[0] === "merchant" ? <MerchantCard merchantId={parts[1] ?? DEFAULT_MERCHANT_ID} /> : null}
      {parts[0] === "reward" ? <RewardDetail rewardId={parts[1] ?? ""} /> : null}
      {parts[0] === "inbox" ? <Inbox /> : null}
      {parts[0] === "profile" ? <Profile /> : null}
      {!parts[0] ? <WalletHome /> : null}
    </AppShell>
  );
}

function WalletHome() {
  const memberships = useQuery({
    queryKey: ["wallet", "memberships"],
    queryFn: api.memberships,
    retry: false
  });
  const walletMemberships = memberships.data ?? [];

  return (
    <>
      <PageHeader title="Your loyalty wallet" kicker="Customer wallet">
        <ButtonLink href="/m/baan-cha?src=wallet" icon="solar:qr-code-bold-duotone">
          Open Baan Cha
        </ButtonLink>
      </PageHeader>
      <MetricStrip
        items={[
          { label: "Joined merchants", value: walletMemberships.length, detail: "Across this wallet", tone: "blue" },
          { label: "Total points", value: walletMemberships.reduce((sum, item) => sum + item.balance, 0), detail: "Live from API", tone: "green" },
          { label: "Rewards", value: walletMemberships.reduce((sum, item) => sum + item.rewardsAvailable, 0), detail: "Available now", tone: "amber" },
          { label: "Expiring", value: walletMemberships.reduce((sum, item) => sum + item.expiringCount, 0), detail: "Needs attention", tone: "red" }
        ]}
      />
      <Surface title="Merchant cards" icon="solar:card-bold-duotone">
        {walletMemberships.length ? (
          <ul className="list">
            {walletMemberships.map((membership) => (
              <li className="list-row" key={membership.membershipId}>
                <div>
                  <strong>{membership.merchantName}</strong>
                  <span>{membership.balance} points · {membership.rewardsAvailable} rewards</span>
                </div>
                <ButtonLink href={`/merchant/${membership.merchantId}`} variant="secondary" icon="solar:alt-arrow-right-linear">
                  Open
                </ButtonLink>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState icon="solar:card-line-duotone" title="No merchant cards" body="Join a merchant to add the first card." />
        )}
      </Surface>
    </>
  );
}

function MerchantLanding({ slug }: { slug: string }) {
  const queryClient = useQueryClient();
  const merchant = useQuery({
    queryKey: ["public-merchant", slug],
    queryFn: () => api.merchantBySlug(slug)
  });
  const join = useMutation({
    mutationFn: ({ marketing, turnstileToken }: { marketing: boolean; turnstileToken?: string }) =>
      api.join(slug, marketing, turnstileToken),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["wallet", "memberships"] });
    }
  });

  function submitJoin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    join.mutate({
      marketing: form.get("marketing") === "on",
      turnstileToken: String(form.get("turnstileToken") ?? "")
    });
  }

  if (!merchant.data) {
    return (
      <>
        <PageHeader title="Merchant" kicker="Merchant QR landing" />
        <Surface title="Join offer" icon="solar:cup-hot-bold-duotone">
          <EmptyState icon="solar:shop-line-duotone" title="Merchant unavailable" body="This merchant page is not available." />
        </Surface>
      </>
    );
  }

  return (
    <>
      <PageHeader title={merchant.data.name} kicker="Merchant QR landing">
        <StatusPill tone={merchant.data.status === "active" ? "green" : "amber"}>{merchant.data.status}</StatusPill>
      </PageHeader>
      <section className="grid-two">
        <Surface title="Join offer" icon="solar:cup-hot-bold-duotone">
          <p className="muted">{merchant.data.primaryOffer}</p>
          <div className="detail-row">
            <span>Earn rule</span>
            <strong>{merchant.data.earnLabel}</strong>
          </div>
          <div className="detail-row">
            <span>Reward</span>
            <strong>{merchant.data.rewardLabel}</strong>
          </div>
          <form className="form-grid" onSubmit={submitJoin}>
            <label className="field wide">
              <span>Consent</span>
              <select name="consent" defaultValue="accept">
                <option value="accept">Accept terms and privacy policy</option>
              </select>
            </label>
            <label className="detail-row wide">
              <span>In-wallet offers</span>
              <input aria-label="Marketing preference" name="marketing" type="checkbox" defaultChecked />
            </label>
            <TurnstileChallenge siteKey={TURNSTILE_SITE_KEY} action="wallet_join" />
            <Button type="submit" icon="solar:user-plus-bold-duotone" disabled={join.isPending}>
              Join wallet
            </Button>
          </form>
          {join.data ? <p className="tone-green">Joined. Membership {join.data.membershipId}</p> : null}
        </Surface>
        <Surface title="Counter QR" icon="solar:qr-code-bold-duotone">
          <div className="qr-block">
            <span>{merchant.data.slug}</span>
          </div>
          <p className="muted">Public QR links only attribute source. Earning and redemption still require server validation.</p>
        </Surface>
      </section>
    </>
  );
}

function MerchantCard({ merchantId }: { merchantId: string }) {
  const memberships = useQuery({ queryKey: ["wallet", "memberships"], queryFn: api.memberships, retry: false });
  const rewards = useQuery({ queryKey: ["rewards", merchantId], queryFn: () => api.rewards(merchantId) });
  const walletMemberships = memberships.data ?? [];
  const merchantRewards = rewards.data ?? [];
  const membership = walletMemberships.find((item) => item.merchantId === merchantId);

  if (!membership) {
    return (
      <>
        <PageHeader title="Loyalty card" kicker="Wallet" />
        <Surface title="Merchant card" icon="solar:card-bold-duotone">
          <EmptyState icon="solar:card-line-duotone" title="Card unavailable" body="This wallet does not have an active card for this merchant." />
        </Surface>
      </>
    );
  }

  return (
    <>
      <PageHeader title={membership.merchantName} kicker="Loyalty card">
        <ButtonLink href="/inbox" variant="secondary" icon="solar:inbox-bold-duotone">Inbox</ButtonLink>
      </PageHeader>
      <Surface title="Balance" icon="solar:medal-star-bold-duotone">
        <ProgressMeter value={membership.balance} max={120} label="Next reward" />
      </Surface>
      <Surface title="Rewards" icon="solar:gift-bold-duotone">
        <ul className="list">
          {merchantRewards.map((reward) => (
            <li className="list-row" key={reward.id}>
              <div>
                <strong>{reward.name}</strong>
                <span>{reward.description}</span>
              </div>
              <ButtonLink href={`/reward/${reward.id}?merchant=${merchantId}`} variant="secondary" icon="solar:ticket-sale-bold-duotone">
                {reward.pointsCost} pts
              </ButtonLink>
            </li>
          ))}
        </ul>
      </Surface>
    </>
  );
}

function RewardDetail({ rewardId }: { rewardId: string }) {
  const merchantId = new URLSearchParams(window.location.search).get("merchant") ?? DEFAULT_MERCHANT_ID;
  const memberships = useQuery({ queryKey: ["wallet", "memberships"], queryFn: api.memberships, retry: false });
  const rewards = useQuery({ queryKey: ["rewards", merchantId], queryFn: () => api.rewards(merchantId) });
  const reward = rewards.data?.find((item) => item.id === rewardId);
  const membership = memberships.data?.find((item) => item.merchantId === merchantId);
  const redeem = useMutation({
    mutationFn: (turnstileToken?: string) =>
      reward && membership
        ? api.redemptionIntent({
            merchantId,
            branchId: membership.defaultBranchId ?? DEFAULT_BRANCH_ID,
            customerId: membership.customerId,
            membershipId: membership.membershipId,
            programmeId: membership.programmeId,
            rewardId: reward.id,
            pointsCost: reward.pointsCost,
            idempotencyKey: idempotencyKey("redemption"),
            turnstileToken
          })
        : Promise.reject(new Error("Reward is unavailable"))
  });

  function submitRedemption(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    redeem.mutate(String(form.get("turnstileToken") ?? ""));
  }

  if (!reward || !membership) {
    return (
      <>
        <PageHeader title="Reward detail" kicker="Wallet" />
        <Surface title="Redemption" icon="solar:ticket-bold-duotone">
          <EmptyState icon="solar:ticket-sale-line-duotone" title="Reward unavailable" body="This reward is not available for the current wallet." />
        </Surface>
      </>
    );
  }

  return (
    <>
      <PageHeader title={reward.name} kicker="Reward detail">
        <StatusPill tone={membership.balance >= reward.pointsCost ? "green" : "red"}>
          {membership.balance >= reward.pointsCost ? "Ready" : "Need points"}
        </StatusPill>
      </PageHeader>
      <Surface title="Redemption" icon="solar:ticket-bold-duotone">
        <p>{reward.description}</p>
        <ProgressMeter value={membership.balance} max={reward.pointsCost} label="Required points" />
        <form className="form-grid" onSubmit={submitRedemption}>
          <TurnstileChallenge siteKey={TURNSTILE_SITE_KEY} action="redemption_intent" />
          <Button type="submit" icon="solar:qr-code-bold-duotone" disabled={redeem.isPending || membership.balance < reward.pointsCost}>
            Create short-lived code
          </Button>
        </form>
        {redeem.data ? (
          <div className="detail-row">
            <span>Show this code to staff before {new Date(redeem.data.expiresAt).toLocaleTimeString()}</span>
            <strong>{redeem.data.redemptionToken.slice(0, 10).toUpperCase()}</strong>
          </div>
        ) : null}
      </Surface>
    </>
  );
}

function Inbox() {
  const inbox = useQuery({ queryKey: ["wallet", "inbox"], queryFn: api.inbox, retry: false });
  const messages = inbox.data ?? [];
  return (
    <>
      <PageHeader title="Wallet inbox" kicker="Merchant messages" />
      <Surface title="Offers" icon="solar:letter-bold-duotone">
        {messages.length ? (
          <ul className="list">
            {messages.map((message) => (
              <li className="list-row" key={message.id}>
                <div>
                  <strong>{message.title}</strong>
                  <span>{message.merchantName} · {message.body}</span>
                </div>
                <Icon icon="solar:alt-arrow-right-linear" width="22" height="22" />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState icon="solar:inbox-line-duotone" title="No messages" body="Merchant wallet messages will appear here." />
        )}
      </Surface>
    </>
  );
}

function Profile() {
  const session = useQuery({ queryKey: ["auth", "session"], queryFn: api.session, retry: false });
  const login = useMutation({
    mutationFn: ({ email, turnstileToken }: { email: string; turnstileToken?: string }) => api.login(email, turnstileToken)
  });
  const logout = useMutation({ mutationFn: api.logout });

  function submitLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    login.mutate({
      email: String(form.get("email") ?? ""),
      turnstileToken: String(form.get("turnstileToken") ?? "")
    });
  }

  return (
    <>
      <PageHeader title="Profile and privacy" kicker="Customer controls" />
      <Surface title="Session" icon="solar:login-3-bold-duotone">
        <form className="form-grid" onSubmit={submitLogin}>
          <Field label="Email"><input name="email" type="email" defaultValue="customer@example.com" /></Field>
          <TurnstileChallenge siteKey={TURNSTILE_SITE_KEY} action="customer_login" />
          <Button type="submit" icon="solar:login-3-bold-duotone" disabled={login.isPending}>Sign in</Button>
          <Button type="button" variant="secondary" icon="solar:logout-3-bold-duotone" onClick={() => logout.mutate()} disabled={logout.isPending}>Logout</Button>
        </form>
        <div className="detail-row">
          <span>Current session</span>
          <strong>{login.data?.userId ?? session.data?.userId ?? "No active session"}</strong>
        </div>
      </Surface>
      <Surface title="Linked identity" icon="solar:shield-user-bold-duotone">
        <div className="detail-row">
          <span>Primary method</span>
          <strong>Email</strong>
        </div>
        <div className="detail-row">
          <span>Marketing in wallet</span>
          <StatusPill tone="green">Granted</StatusPill>
        </div>
        <Field label="Data request">
          <select defaultValue="export">
            <option value="export">Export my wallet data</option>
            <option value="delete">Request deletion</option>
          </select>
        </Field>
      </Surface>
    </>
  );
}
